/**
 * Traduz recusa de PERMISSAO do Postgres para uma frase que o usuario entende.
 *
 * ─── DE ONDE VEIO ───────────────────────────────────────────────────────────
 *
 * 26/08/2026, Arena 015: a recepcionista montou uma aula inteira — dia,
 * horario, professor, quadra, modalidade, vagas — clicou em Criar e leu na
 * tela, em ingles e com nome de tabela:
 *
 *   new row violates row-level security policy for table "club_lessons"
 *
 * ─── POR QUE ISTO E CENTRAL, E NAO UM `catch` NA TELA ───────────────────────
 *
 * Porque nao e uma tela. A varredura de 26/08 achou 25 das 35 rotas do CRM que
 * abrem com `.view` e oferecem botao de escrita SEM checar `.manage` — e toda
 * familia de permissao tem pelo menos um papel que ve e nao gerencia, entao
 * cada uma delas tem alguem que chega ate o clique e leva a recusa.
 *
 * Gatear as 25 telas e o conserto certo, e esta em curso. Mas leva varios PRs,
 * e ate o ultimo entrar as telas continuam falhando. Aqui, num lugar so, elas
 * passam a falhar EM PORTUGUES E DIZENDO O QUE FAZER. E quando uma tela nova
 * nascer sem gate — vai nascer —, ela ja nasce com esta rede embaixo.
 *
 * ─── O QUE ESTA FUNCAO DELIBERADAMENTE NAO FAZ ──────────────────────────────
 *
 * Nao promete que a acao vai funcionar depois, nao sugere "tente novamente" e
 * nao chuta qual permissao falta. O usuario nao tem como saber, e mandar
 * repetir uma acao que o banco recusou por politica so gera o terceiro clique
 * — que foi exatamente o formato do incidente anterior deste clube.
 *
 * Qualquer erro que NAO seja de permissao passa intacto. Um tradutor que
 * engole a mensagem original torna todo o resto do sistema mais dificil de
 * depurar, e o ganho aqui e restrito a uma familia de erro bem identificada.
 */

/** Nome de tabela → como o clube chama aquilo. Só o que o usuário toca. */
const ASSUNTO: Record<string, string> = {
  club_lessons: 'aulas',
  club_lesson_students: 'matrículas em aula',
  lesson_attendance: 'chamada',
  club_assessment_slots: 'horários de nivelamento',
  club_students: 'alunos',
  club_teachers: 'professores',
  club_courts: 'quadras',
  club_tuition_plans: 'planos',
  student_invoices: 'faturas',
  club_expenses: 'despesas',
  club_products: 'produtos',
  club_sales: 'vendas do PDV',
  club_sale_items: 'itens da venda',
  club_staff: 'equipe',
  club_role_permissions: 'permissões',
  clubs: 'dados do clube',
  leads: 'leads',
  opportunities: 'oportunidades',
  tournaments: 'torneios',
  club_announcements: 'comunicados',
  club_integrations: 'integrações',
  makeup_requests: 'reposições',
  trial_lessons: 'aulas experimentais',
  club_members: 'membros',
  bookings: 'reservas',
};

/**
 * As tres formas que a recusa chega. Sao mensagens do Postgres, estaveis o
 * bastante para casar por texto — e o `code` nem sempre vem junto, porque no
 * caminho comum da base (`const { error } = await supabase...`) o erro e o
 * objeto plano do corpo HTTP, nao um `PostgrestError`.
 */
const NOVA_LINHA = /new row violates row-level security policy for table "([^"]+)"/i;
const SEM_POLICY = /violates row-level security policy(?: for table "([^"]+)")?/i;
const SEM_GRANT = /permission denied for (?:table|relation|view|function) "?([\w.]+)"?/i;

export type RecusaDePermissao = {
  /** Tabela que recusou, quando a mensagem a informa. */
  tabela: string | null;
  /** Como o clube chama aquilo, quando conhecido. */
  assunto: string | null;
  mensagem: string;
};

/**
 * Devolve a recusa traduzida, ou `null` quando o erro NAO e de permissao.
 * `null` e a resposta certa para "não é comigo" — quem chama deixa a mensagem
 * original passar.
 */
export function recusaDePermissao(erro: unknown): RecusaDePermissao | null {
  const bruto = mensagemBruta(erro);
  if (!bruto) return null;

  const codigo = codigoDoErro(erro);
  const casou =
    NOVA_LINHA.exec(bruto) ?? SEM_POLICY.exec(bruto) ?? SEM_GRANT.exec(bruto);

  // `42501` é `insufficient_privilege`. Vale sozinho: há recusas com esse
  // código cuja mensagem não menciona política nenhuma.
  if (!casou && codigo !== '42501') return null;

  const tabela = casou?.[1] ? casou[1].replace(/^public\./, '') : null;
  const assunto = tabela ? (ASSUNTO[tabela] ?? null) : null;

  return { tabela, assunto, mensagem: frase(assunto) };
}

function frase(assunto: string | null): string {
  const onde = assunto ? ` em ${assunto}` : '';
  return (
    `Seu perfil não tem permissão para esta ação${onde}. ` +
    'Quem administra o clube pode liberar em Equipe → Permissões.'
  );
}

function mensagemBruta(erro: unknown): string | null {
  if (typeof erro === 'string') return erro;
  if (erro instanceof Error) return erro.message;
  if (
    erro &&
    typeof erro === 'object' &&
    'message' in erro &&
    typeof (erro as { message: unknown }).message === 'string'
  ) {
    return (erro as { message: string }).message;
  }
  return null;
}

function codigoDoErro(erro: unknown): string | null {
  if (
    erro &&
    typeof erro === 'object' &&
    'code' in erro &&
    typeof (erro as { code: unknown }).code === 'string'
  ) {
    return (erro as { code: string }).code;
  }
  return null;
}
